import { InvestorDetailsActivityTabListEntityTransactionsQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { getUTCDate } from '@/shared/utils/date';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import { activityTableColumns } from '../../constants';

interface ActivityMobileTableProps {
  tableRows: InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data'];
  entityType: InvestorDetailsEntityTypeEnum;
  openOpportunityDetailsPage: (id: string) => void;
}

const ActivityMobileTable = ({ tableRows, entityType, openOpportunityDetailsPage }: ActivityMobileTableProps) => {
  const amountToolTipContent = (row: InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data'][number]) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>Investment Amount: {currencyFormatter.format(row.amount)}</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>Total Amount incl Fees: {currencyFormatter.format(row.totalAmount)}</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  const TableRow = ({ el }: { el: InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data'][number] }) => {
    return (
      <MobileTableRowWrap key={guid()}>
        <MobileRow>
          <MobileRowTitle>{activityTableColumns[0].title}</MobileRowTitle>
          <MobileRowValue>{getUTCDate(el.date)}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{activityTableColumns[1].title}</MobileRowTitle>
          <MobileRowValueWrapper>
            <StrategyName isRedirect onClick={() => openOpportunityDetailsPage(el.investmentId)}>
              {el.strategy}
            </StrategyName>
            {(entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group) && (
              <EntityName>{el.entityName}</EntityName>
            )}
          </MobileRowValueWrapper>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{activityTableColumns[2].title}</MobileRowTitle>
          <MobileRowValue>{el.transactionType}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{activityTableColumns[3].title}</MobileRowTitle>
          <MobileRowValue>
            <CellWithTooltip>
              {formatCash(el.amount)}
              <Tooltip title={amountToolTipContent(el)}>
                <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
              </Tooltip>
            </CellWithTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{activityTableColumns[4].title}</MobileRowTitle>
          <MobileRowValue>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.distributionsToDate}</FormattingTooltip>
            </MobileRowValue>
          </MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{activityTableColumns[5].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{el.remainingCapital}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{activityTableColumns[6].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{el.marketValue}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {tableRows?.length ? (
        tableRows.map((el, index) => {
          return <TableRow el={el} key={index} />;
        })
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default ActivityMobileTable;

const MobileRowValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 50%;
`;

const StrategyName = styled(MobileRowValue)`
  width: 100%;
`;

const EntityName = styled.p`
  color: ${themeVar('font.strong')};
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
